import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { MailOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card } from 'antd';
import { useParams } from 'react-router';

import { useLazyGetSchoolParticipantsQuery } from '@/api/endpoints/schools/schoolParticipants';
import type { EParticipantsStatus } from '@/shared/consts';
import { useApiRequest, useLoader } from '@/shared/hooks';

import { ETabsKeys } from '../../SchoolEditPage/consts';
import { SchoolEditPageTabContext } from '../../SchoolEditPage/SchoolEditPageTabContext';
import { SendInvitesModal } from '../Modals/SendInvitesModal/SendInvitesModal';
import { ParticipantsContext } from '../ParticipantsSection/ParticipantsContext';

import { getStatCardsData } from './helpers';
import styles from './ParticipantsStat.module.scss';

export const ParticipantsStat = () => {
  const [isSendInvitesModalOpen, setIsSendInvitesModalOpen] = useState(false);

  const { setActiveTab } = useContext(SchoolEditPageTabContext);
  const { pagination, filters, searchValue, setParticipantsTableData, participantsStat } =
    useContext(ParticipantsContext);

  const { id: schoolId } = useParams();
  const sendApiRequest = useApiRequest();

  const [getParticipants, { isFetching: areParticipantsFetching }] = useLazyGetSchoolParticipantsQuery();

  const openSendInvitesModal = useCallback(() => setIsSendInvitesModalOpen(true), []);
  const closeSendInvitesModal = useCallback(() => setIsSendInvitesModalOpen(false), []);

  const statCardsData = useMemo(() => getStatCardsData({ participantsStat }), [participantsStat]);

  const { showLoader, hideLoader } = useLoader();

  const resetHandle = () => {
    sendApiRequest({
      apiRequestCallback: () =>
        getParticipants({
          schoolId: Number(schoolId),
          page: pagination?.current,
          perPage: pagination?.pageSize,
          statusFilter: filters.status as EParticipantsStatus[],
          nameOrEmailSearchString: searchValue,
        }),
      successCallback: (response) => setParticipantsTableData(response.data),
    });
  };

  useEffect(() => {
    if (areParticipantsFetching) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [areParticipantsFetching]);

  return (
    <>
      <SendInvitesModal
        isModalOpen={isSendInvitesModalOpen}
        handleCancel={closeSendInvitesModal}
      />
      <div className={styles['header-wrapper']}>
        <div className={styles['title-wrapper']}>
          <p className={styles.title}>Список участников</p>
          <Button
            icon={<ReloadOutlined />}
            shape="circle"
            size="small"
            onClick={resetHandle}
          />
        </div>
        <div className={styles['buttons-wrapper']}>
          <Button
            icon={<MailOutlined />}
            onClick={openSendInvitesModal}
          >
            Отправить приглашение
          </Button>
          <Button
            icon={<DownloadOutlined />}
            type="primary"
            onClick={() => setActiveTab(ETabsKeys.ParticipantsImport)}
          >
            Импорт участников
          </Button>
        </div>
      </div>
      <Card>
        <div className={styles['stat-cards-wrapper']}>
          {statCardsData.map(({ id, title, value }) => (
            <Card
              key={id}
              className={styles['stat-card']}
            >
              {title}: {value}
            </Card>
          ))}
        </div>
      </Card>
    </>
  );
};
